import { forwardRef, type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  type PopoverProps as AriaPopoverProps,
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Autocomplete,
  ButtonContext,
  ListBox,
  SelectValue,
  useFilter,
  type ValidationResult,
} from 'react-aria-components';
import cx from 'clsx';
import { isNil } from 'es-toolkit';
import {
  IconChevronBottomOutlined,
  IconChevronTopOutlined,
  IconCrossOutlined,
  IconInfoRoundedOutlined,
} from '@repo/monochrome-icons';
import { Button } from '#components/button/button';
import { SearchFieldWithDebounce } from '#components/search-field-with-debounce/search-field-with-debounce';
import { SearchField } from '#components/search-field/search-field';
import { Description } from '#components/form-elements/description/description';
import { Error } from '#components/form-elements/error/error';
import { FieldLabel } from '#components/form-elements/label/label';
import { Option } from '#components/form-elements/options/option';
import { OptionDivider } from '#components/form-elements/options/option-divider';
import { OptionGroup } from '#components/form-elements/options/option-group';
import { Trigger } from '#components/form-elements/trigger/trigger';
import { Tooltip } from '#components/tooltip/tooltip';
import {
  DEFAULT_ROW_HEIGHT,
  VirtualizedListLayout,
} from '#components/virtualized-list-layout/virtualized-list-layout';
import { Popover } from '#components/popover/popover';
import { useVisibleRowsCap } from '#hooks/use-visible-rows-cap';
import styles from './select.module.css';

const DEFAULT_VISIBLE_ROWS = 7;

export interface SelectProps<T extends object> extends AriaSelectProps<T> {
  label?: string;
  labelTooltip?: string;
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  multiple?: boolean;
  inputClassName?: string;
  isOptional?: boolean;
  isOptionalText?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  selectedValueClassName?: string;
  customSelectedValueContent?: ReactNode;
  displaySelectedTextValue?: boolean;
  allowClear?: boolean;
  withSearch?: {
    async?: boolean;
    placeholder: string;
    emptyMessage?: string;
    loadingMessage?: string;
    autoFocus?: boolean;
  };
  isLoading?: boolean;
  autocompleteInputValue?: string;
  autocompleteOnInputChange?: (value: string) => void;
  portalContainerId?: string;
  optionListClassName?: string;
  popoverPlacement?: AriaPopoverProps['placement'];
  popoverClass?: string;
  isVirtualized?: boolean;
  /** Number of options fully visible before the list scrolls. */
  visibleRows?: number;
  /** Required when `isVirtualized`, where every row must be the same height. */
  rowHeight?: number;
}

const SelectComponent = forwardRef(function SelectComponent<T extends object>(
  {
    label,
    labelTooltip,
    description,
    errorMessage,
    isOptional,
    isOptionalText,
    children,
    defaultOpen = false,
    isOpen,
    onOpenChange,
    inputClassName,
    isDisabled,
    isReadOnly,
    fullWidth = true,
    selectedValueClassName,
    customSelectedValueContent,
    displaySelectedTextValue,
    allowClear = false,
    withSearch,
    isLoading = false,
    portalContainerId,
    placeholder,
    autocompleteInputValue,
    autocompleteOnInputChange,
    optionListClassName,
    popoverPlacement = 'bottom start',
    popoverClass,
    isVirtualized = false,
    visibleRows = DEFAULT_VISIBLE_ROWS,
    rowHeight = DEFAULT_ROW_HEIGHT,
    ...props
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  const intl = useIntl();
  const { contains } = useFilter({ sensitivity: 'base' });
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const listRef = useRef<HTMLDivElement>(null);

  useVisibleRowsCap({ listRef, isOpen: internalIsOpen, isVirtualized, visibleRows });

  function handleOpenChange(open: boolean): void {
    setInternalIsOpen(open);
    onOpenChange?.(open);
  }

  useEffect(() => {
    if (!isNil(isOpen)) {
      setInternalIsOpen(isOpen);
    }
  }, [isOpen]);

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  const autocompleteProps = withSearch?.async
    ? {
        inputValue: autocompleteInputValue,
        onInputChange: autocompleteOnInputChange,
      }
    : {
        filter: contains,
      };

  const SearchComponent = withSearch?.async ? SearchFieldWithDebounce : SearchField;
  const handleClear = (): void => {
    if (props.onSelectionChange) {
      props.onSelectionChange(null);
    }
  };

  const showClearButton = allowClear && props.selectedKey && !isDisabled && !isReadOnly;

  const optionListStyle = {
    '--select-visible-rows': visibleRows,
    '--select-row-height': `${rowHeight}px`,
  } as React.CSSProperties;

  return (
    <AriaSelect
      {...props}
      data-readonly={String(Boolean(isReadOnly) && !isDisabled)}
      isDisabled={isDisabled || isReadOnly}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placeholder={placeholder}
      ref={ref}
    >
      <div className={styles.container}>
        {Boolean(label) && (
          <div className={styles.labelContainer}>
            <FieldLabel>
              {label} {Boolean(isOptional) && isOptionalText}
            </FieldLabel>
            {Boolean(labelTooltip) && (
              <ButtonContext.Provider value={null}>
                <Tooltip
                  buttonProps={{
                    'aria-label': labelTooltip,
                  }}
                  label={labelTooltip}
                  needsButton
                  placement="top left"
                >
                  <IconInfoRoundedOutlined className={styles.tooltipIcon} />
                </Tooltip>
              </ButtonContext.Provider>
            )}
          </div>
        )}
        <div className={styles['input-container-wrapper']}>
          <Trigger
            className={cx(styles['input-container'], inputClassName)}
            // override the readonly/disabled state defined for the AriaSelect component
            isDisabled={isDisabled ?? false}
            isReadOnly={isReadOnly}
          >
            {customSelectedValueContent ?? (
              <SelectValue
                // Opt out of browser auto-translation: translators re-parent text into <font> nodes,
                // then React's removeChild on the moved node throws and crashes the tree.
                className={cx(
                  styles['input-value'],
                  showClearButton && styles['input-value--with-clear'],
                  selectedValueClassName,
                  'notranslate'
                )}
                translate="no"
              >
                {displaySelectedTextValue
                  ? ({ selectedText, defaultChildren }) => {
                      return selectedText || defaultChildren;
                    }
                  : undefined}
              </SelectValue>
            )}
            {Boolean(!showClearButton && internalIsOpen) && <IconChevronTopOutlined aria-hidden />}
            {!showClearButton && !internalIsOpen && (
              <IconChevronBottomOutlined
                aria-hidden
                className={cx(isReadOnly && styles['chevron-icon--readonly'])}
              />
            )}
          </Trigger>
          {Boolean(showClearButton) && (
            <ButtonContext.Provider value={null}>
              <Button
                aria-label={intl.formatMessage({ id: 'datepicker-btn-clear' })}
                className={styles.clearButton}
                data-testid="select-clear-button"
                iconOnly
                onPress={handleClear}
                size="small"
                variant="tertiary"
              >
                <IconCrossOutlined aria-hidden />
              </Button>
            </ButtonContext.Provider>
          )}
        </div>
        <Error>{errorMessage}</Error>
        {Boolean(description) && (
          <Description data-testid="select-description">{description}</Description>
        )}
      </div>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        // Opt the popover (search + option list) out of browser auto-translation, else translated
        // <font>-wrapped option nodes make React's removeChild throw on unmount. (RAC rejects `translate`.)
        className={cx(
          'body-standard',
          styles.popover,
          fullWidth && styles['full-width'],
          popoverClass,
          'notranslate'
        )}
        data-interactive
        placement={popoverPlacement}
      >
        {withSearch ? (
          <Autocomplete {...autocompleteProps}>
            <SearchComponent
              autoFocus={withSearch.autoFocus}
              className={styles['search-container']}
              inputClassName={styles.search}
              placeholder={withSearch.placeholder}
            />
            <VirtualizedListLayout isVirtualized={isVirtualized} rowHeight={rowHeight}>
              <ListBox
                className={cx(styles['option-list'], optionListClassName)}
                ref={listRef}
                renderEmptyState={() => {
                  if (isLoading && withSearch.loadingMessage) {
                    return <div className={styles['no-results']}>{withSearch.loadingMessage}</div>;
                  }
                  if (withSearch.emptyMessage) {
                    return <div className={styles['no-results']}>{withSearch.emptyMessage}</div>;
                  }
                  return null;
                }}
                style={optionListStyle}
              >
                {children}
              </ListBox>
            </VirtualizedListLayout>
          </Autocomplete>
        ) : (
          <VirtualizedListLayout isVirtualized={isVirtualized} rowHeight={rowHeight}>
            <ListBox
              className={cx(styles['option-list'], optionListClassName)}
              ref={listRef}
              style={optionListStyle}
            >
              {children}
            </ListBox>
          </VirtualizedListLayout>
        )}
      </Popover>
    </AriaSelect>
  );
}) as <T extends object>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement;

export const Select = Object.assign(SelectComponent, {
  Option,
  Divider: OptionDivider,
  Group: OptionGroup,
});
